import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faUpload } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { selectDeleteFile, selectGetAggregateSiteAssessmentInfo, selectGetFile, selectProfileGetOverview, selectSaveData, selectUploadAggregateFile } from "../../redux/selectors/exportSelectorPaths";
import { AggregateSiteAssessmentInfoState } from "../../types/assessment_info/AggregateSiteAssessmentInfoState";
import { getAssessmentInfo } from "../../redux/actions/assessmentInfo/assessmentInfo";
import { ProfileOverviewState } from "../../redux/reducers/getProfile/getProfileOverviewReducer";
import LoadingSpinner from "../../components/common/LoadingSpinner/LoadingSpinner";
import { FileStatusBadge } from "../../components/common/FileStatusBadge/FileStatusBadge";
import ErrorMessages from "../../components/common/ErrorMessages/ErrorMessages";
import { exactMatcher, FormError, getFormErrorMessageByName, hasError, useFormErrorContext, useFormErrorValidationResponse } from "../../utils/context/FormErrorContext";
import { useDropzone } from "react-dropzone";
import { resetSaving, setSaveResultSuccess } from "../../redux/actions/saveData/saveData";
import { DECREMENT_UPLOAD_COUNT, RESET_FILE } from "../../redux/actionTypes/actionsTypes";
import { uploadAggregateFile } from "../../redux/actions/assessmentInfo/uploadAggregateFile";
import { deleteFile, getFile } from "../../redux/actions/file/file";
import { FileState } from "../../redux/reducers/getFile/getFileReducer";
import { toast } from "react-toastify";
import { DeleteFileState } from "../../redux/reducers/getFile/deleteFileReducer";
import ConfirmationDialog from "../../components/common/Modal/ConfirmationDialog";
import { Trans, useTranslation } from "react-i18next";
import "./AggregateFileUpload.css";
import { SaveDataState } from "../../redux/reducers/getSaveData/saveDataReducer";
import { UploadAggregateFileState } from "../../redux/reducers/getAggregateSiteAssessmentInfo/getUploadAggregateFileReducer";
import { useReadOnly } from "../../utils/ReadOnlyContext";
import { MBToBytes } from "../../utils/fileSizeConverter";
import { useNavigation } from "../../utils/context/NavigationContext";
import { Attachment, PendingFile } from "../../types/attachments/Attachment";
import { setNextActiveMenuItem, setPrevActiveMenuItem } from "../../redux/actions/menu/menu";
import { initialMenuItem } from "../../redux/reducers/getMenu/getMenuReducer";

const MAX_FILES = 5;
const MAX_SIZE_MB = 5
const MAX_SIZE = MBToBytes(MAX_SIZE_MB + 1);
const AGGREGATE_ALLOWED_EXTENSIONS = {
  "image/jpeg": [".jpeg", ".jpg"],
  "application/pdf": [".pdf"],
  "application/msword": [".doc"], // Word 97-2003
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"], // Word 2007+
  "application/vnd.google-earth.kmz": [".kmz"], // KMZ (zipped)
  "application/vnd.google-earth.kml+xml": [".kml"], // KML (unzipped)
  "application/acad": [".dwg"], // AutoCAD DWG files
  "application/x-autocad": [".dwg"], // sometimes AutoCAD uses this MIME
}

export default function AggregateFileUpload() {
  const dispatch = useDispatch();
  const { t } = useTranslation("common")
  const currentSnapshotId = sessionStorage.getItem("currentSnapshotId") as string;
  const rollNumber = sessionStorage.getItem("rollNumber") as string;

  const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
  const uploadFileState: UploadAggregateFileState = useSelector(selectUploadAggregateFile);
  const getAssessmentInfoState: AggregateSiteAssessmentInfoState = useSelector(selectGetAggregateSiteAssessmentInfo);
  const fileState: FileState = useSelector(selectGetFile);
  const deleteFileState: DeleteFileState = useSelector(selectDeleteFile);
  const saveData: SaveDataState = useSelector(selectSaveData);
  const { isReadOnly } = useReadOnly();
  const { direction } = useNavigation();
  const { clearErrors, errors, addError } = useFormErrorContext()

  const [downloading, setDownloading] = useState(false)
  const [deleting, setDeleting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<Attachment[]>([]);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [activeDeleteFile, setActiveDeleteFile] = useState<string | null>(null);

  const isUploadActive = uploadFileState.activeUploads > 0;
  const campaignStatusCode = sessionStorage.getItem("campaignStatusCode") ?? ""
  const validationResult = uploadFileState?.result ?? [];
  const disableDropzone = isReadOnly || (campaignStatusCode !== "EXTERNAL_IN_PROGRESS");

  useFormErrorValidationResponse(validationResult);

  const uploadFolderKey = `${rollNumber}_${profileOverview.profile.profile_id}`;

  useEffect(() => {
    dispatch(getAssessmentInfo(currentSnapshotId, profileOverview.profile.profile_type_code));
    setUploadedFiles([]);
    clearErrors();
    // eslint-disable-next-line 
  }, []);


  useEffect(() => {
    if (!getAssessmentInfoState.loading && getAssessmentInfoState.success && "attachments" in getAssessmentInfoState.formData) {
      if (getAssessmentInfoState.formData.attachments) {
        const attachmentsFromBackend = getAssessmentInfoState.formData.attachments;
        setUploadedFiles(attachmentsFromBackend);
      }
    }
  }, [getAssessmentInfoState])

  const onDrop = useCallback((acceptedFiles: File[]) => {
    clearErrors();

    const newPending: PendingFile[] = acceptedFiles.map(file => ({
      file: new File([file], file.name.replace(/\s+/g, "_"), {
        type: file.type,
        lastModified: file.lastModified,
      }),
      clientId: String(crypto.randomUUID()), // to keep an identifier of new files, handle scenario with duplicate file names
      componentId: null,
    }));

    setPendingFiles(prev => [...prev, ...newPending]);
  }, [clearErrors]);

  // main logic is here
  // regardless of uploadFileState status - update the uploaded files state to view latest uploaded files
  // if uploadFileState has errors, then enrich pendingFiles with the componentid to map errors
  // if uploadFileState is successful, then remove the element from pendingfiles list
  useEffect(() => {
    if (uploadFileState.activeUploads === 0) {
      if (uploadFileState.attachments && uploadFileState.attachments.length > 0) {
        const response = uploadFileState.attachments;
        const componentMap = new Map<string, string>();
        setUploadedFiles(
          response.filter(item => !item.clientId)
        );

        if (uploadFileState.status === 200) {
          const uploadedFileNames = new Set(
            response
              .filter(a => a.fileName)
              .map(a => a.fileName)
          );

          setPendingFiles(prev =>
            prev.filter(file => !uploadedFileNames.has(file.file.name))
          );
        }
        else {
          response.forEach(item => {
            if (item.clientId && item.componentId) {
              componentMap.set(item.clientId, item.componentId);
            }
          });

          setPendingFiles(prev => {
            const componentMap = new Map<string, string>();

            response.forEach(item => {
              if (item.clientId && item.componentId) {
                componentMap.set(item.clientId, item.componentId);
              }
            });

            return prev.map(file => {
              if (!file.clientId) return file;

              return {
                ...file,
                componentId: componentMap.get(file.clientId) ?? file.componentId ?? null,
              };
            });
          });
        }

      }
    }
  }, [uploadFileState.activeUploads, uploadFileState.attachments, uploadFileState.status])


  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: AGGREGATE_ALLOWED_EXTENSIONS,
    maxSize: MAX_SIZE,
    multiple: false,
    disabled: disableDropzone
  });

  const uploadFiles = useCallback(() => {
    clearErrors();
    const queue = pendingFiles.filter(pf => pf.componentId === null);
    while (queue.length > 0) {
      const pf = queue.shift();

      if (!pf) return;
      const formData = new FormData();
      formData.append("file", pf.file);
      formData.append("clientId", pf.clientId);
      try {
        dispatch(
          uploadAggregateFile(uploadFolderKey, currentSnapshotId, campaignStatusCode, formData)
        );
      } catch (err: unknown) {
        if (err instanceof Error) {
          console.error("Upload failed:", err.message);
        } else {
          console.error("Upload failed:", err);
        }
      }
    }
    // specify pendingFiles.length instead of pendingFiles to avoid infinite loop when pendingFiles is enriched with componentId when upload response is received with errors
    // eslint-disable-next-line
  }, [currentSnapshotId, dispatch, pendingFiles.length, clearErrors, campaignStatusCode, uploadFolderKey]);

  useEffect(() => {
    if (pendingFiles.length > 0) {
      try {
        uploadFiles();
      } catch (err) {
        dispatch({
          type: DECREMENT_UPLOAD_COUNT,
        });
      }
    }

  }, [pendingFiles.length, dispatch, uploadFiles])

  // If uploadFileState has errors then don't let user navigate forward, backward navigation allowed
  useEffect(() => {
    if (saveData.saving) {
      dispatch(resetSaving())
      if (uploadFileState.status === 200 || errors.length === 0) {
        dispatch(setSaveResultSuccess());
        return
      }
      else if (uploadFileState.status !== 200 && (uploadFileState.result && uploadFileState.result.length > 0)) {
        if (direction !== "backward") {
          dispatch(setNextActiveMenuItem(initialMenuItem))
          dispatch(setPrevActiveMenuItem(initialMenuItem))
        }
        else {
          dispatch(setSaveResultSuccess())
          clearErrors()
        }
      }
    }
  }, [saveData, uploadFileState, dispatch, direction, clearErrors, pendingFiles.length, uploadFiles, errors.length])


  const removeFile = (clientId: string) => {
    clearErrors();
    setPendingFiles(prev =>
      prev.filter(pf => pf.clientId !== clientId)
    );
  };

  const downloadFile = (fileName: string) => {
    dispatch(getFile(uploadFolderKey, currentSnapshotId, fileName, campaignStatusCode));
    setDownloading(true);
  }

  useEffect(() => {
    if (downloading && fileState.loading === false) {
      if (fileState.status === 200) {
        const url = fileState.s3url
        const downloadLink = document.createElement("a")
        downloadLink.href = url
        downloadLink.target = "_blank"
        downloadLink.download = fileState.fileName
        downloadLink.click()
      } else {
        const newError: FormError = {
          primaryKey: fileState.fileName,
          level: 'error',
          message: t("file-upload.upload-failed-message"),
          messageFr: t("file-upload.upload-failed-message")
        }
        addError(newError);
      }
      dispatch({ type: RESET_FILE })
      setDownloading(false)
    }
  }, [downloading, fileState, dispatch, t, addError]);

  const deleteFileFromServer = (fileName: string) => {
    setShowConfirmDeleteModal(false);
    dispatch(deleteFile(uploadFolderKey, currentSnapshotId, fileName, campaignStatusCode));
    setDeleting(true);
    clearErrors();
  }

  useEffect(() => {
    if (deleting && !deleteFileState.loading) {
      if (!deleteFileState.deleted) {
        toast.error('Failed to delete file. Please try again.')
      }
      else {
        dispatch(getAssessmentInfo(currentSnapshotId, profileOverview.profile.profile_type_code))
      }

      setDeleting(false);
    }
  }, [deleting, deleteFileState, currentSnapshotId, dispatch, profileOverview.profile.profile_type_code])

  const fileHasErrors = (file: Attachment | PendingFile): boolean => {
    let hasErrors = false;
    if (
      hasError(errors, `assessmentInfo.attachments.${file.componentId}.fileName`, exactMatcher) ||
      hasError(errors, `assessmentInfo.attachments.${file.componentId}.fileSize`, exactMatcher)) {
      hasErrors = true
    }
    return hasErrors
  }

  const handleErrorOnClickByComponentId = (error: FormError) => {
    const findById = document.getElementById(error.primaryKey.split(".")[2]);
    findById?.scrollIntoView({ behavior: 'smooth' })
  }

  return (<>
    <LoadingSpinner loading={isUploadActive || getAssessmentInfoState.loading || downloading || deleting}>
      <div className="file-upload-description">
        <p>{t("file-upload.file-upload-description")}</p>
        <ul>
          <li>{t("file-upload.example1")}</li>
          <li>{t("file-upload.example2")}</li>
          <li>{t("file-upload.example3")}</li>
          <li>{t("file-upload.example4")}</li>
          <li>{t("file-upload.example5")}</li>
        </ul>
        {errors.length > 0 && (
          <ErrorMessages
            errors={errors}
            onErrorClick={handleErrorOnClickByComponentId}
          />
        )}
        <div
          {...getRootProps()}
          className={`file-dropzone ${isDragActive ? "drag-active" : ""} ${disableDropzone ? "inactive" : ""}`}
        >
          <FontAwesomeIcon icon={faUpload} className="upload-icon" />
          <input {...getInputProps()} />
          <p>{t("file-upload.drag-drop-select-files-here")}</p>
          <p>{t("file-upload.max-files-per-file-size", {
            maxFiles: MAX_FILES,
            maxSizeMB: MAX_SIZE_MB
          })}</p>
          <p>{t("file-upload.accepted-file-types-label")}</p>
        </div>

        {/* Pending File list */}
        <div className="file-list">
          {(pendingFiles.length > 0 || uploadedFiles.length > 0) ?
            <p className="current">{t("file-upload.file-count-label")}&nbsp;{(pendingFiles.length + uploadedFiles.length)}</p> :
            <p>{t("file-upload.no-files-uploaded")}</p>}

          {pendingFiles.map((pf, index) => (
            <div key={`${pf.file.name}-${index}`}
              id={pf.componentId ?? ""}
              className={fileHasErrors(pf) ? 'file-item table-error' : 'file-item'}>
              <div>
                <div>{pf.file.name}</div>
                <small>{pf.file.size > 0 && (pf.file.size / 1024 / 1024).toFixed(2)} MB</small>
              </div>
              <FileStatusBadge status="pending" />
              {!isReadOnly && <button
                onClick={() => {
                  removeFile(pf.clientId)
                }}
                aria-label={`${index}-delete-icon`}>
                <FontAwesomeIcon icon={faTrash} />
              </button>}
              {fileHasErrors(pf) && <p className="form-error-message">
                {getFormErrorMessageByName(errors, `assessmentInfo.attachments.${pf.componentId}.fileName`, exactMatcher)}
                {getFormErrorMessageByName(errors, `assessmentInfo.attachments.${pf.componentId}.fileSize`, exactMatcher)}
              </p>}
            </div>
          ))}
          {/* Uploaded File list */}
          {uploadedFiles.map((file, index) => (
            <div
              key={`${file.fileName}-${index}`}
              id={file.componentId ?? ""}
              className={fileHasErrors(file) ? 'file-item table-error' : 'file-item'}>
              <div className="file-name-section"
                onClick={() => file.fileName && downloadFile(file.fileName)}
              >
                <div className="file-name">{file.fileName}</div>
                <small>{file.fileSize && (file.fileSize / 1024 / 1024).toFixed(2)} MB</small>
              </div>
              <FileStatusBadge status={"uploaded"} />
              {(!isReadOnly && campaignStatusCode === "EXTERNAL_IN_PROGRESS") && <button
                onClick={() => {
                  setShowConfirmDeleteModal(true);
                  setActiveDeleteFile(file.fileName)
                }}
                aria-label={`${index}-delete-icon`}>
                <FontAwesomeIcon icon={faTrash} />
              </button>}
              {fileHasErrors(file) && <p className="form-error-message">
                {getFormErrorMessageByName(errors, `assessmentInfo.attachments.${file.componentId}.fileName`, exactMatcher)}
                {getFormErrorMessageByName(errors, `assessmentInfo.attachments.${file.componentId}.fileSize`, exactMatcher)}
              </p>}
            </div>
          ))}
          {showConfirmDeleteModal &&
            <ConfirmationDialog
              onCancel={() => setShowConfirmDeleteModal(false)}
              onConfirm={() => activeDeleteFile && deleteFileFromServer(activeDeleteFile)}
              headerClass="no-border"
              buttonGroupClass="justify-content-center no-border"
              title={t("file-upload.confirm-delete-attachment-heading")}
            >
              <Trans
                t={t}
                i18nKey="file-upload.confirm-delete-attachment-body"
                components={{ strong: <strong /> }}
                values={{ fileName: activeDeleteFile }}
              />
            </ConfirmationDialog>
          }
        </div>
      </div>
    </LoadingSpinner>

  </>)
}